import React from 'react';
import { motion } from 'framer-motion';
import { useForm, ValidationError } from '@formspree/react';
import { SectionLabel, SectionHeading } from './ui/UI';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
const focus = ['Attack-path reporting', 'AI app validation', 'Fix verification', 'Evidence packs', 'Self-serve onboarding', 'SaaS-style pricing', 'MSSP workflows', 'Internal runner demand', 'Customer security review use cases'];
const ideal = ['B2B SaaS companies', 'AI-native companies', 'Security teams preparing for customer reviews', 'Companies approaching SOC 2 or cyber-insurance renewal', 'MSSPs and vCISOs', 'Teams launching AI applications', 'Fortune 10,000 business units needing faster validation'];
export function EarlyAccess() {
  const [state, handleSubmit] = useForm('mykvgpkn');
  const {
    submitting,
    succeeded
  } = state;
  return <section id="early-access" className="relative w-full py-32 border-t border-white/5" data-id="element-353">
      <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-354">
        <SectionLabel number="20" label="Early Access" className="mb-8" data-id="element-355" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16" data-id="element-356">
          <div className="lg:col-span-7" data-id="element-357">
            <SectionHeading size="xl" data-id="element-358">Request early access.</SectionHeading>
          </div>
          <div className="lg:col-span-5 lg:pt-4" data-id="element-359">
            <p className="text-lg text-white/65 leading-relaxed" data-id="element-360">
              Tell us where to reach you and what you need to validate. We'll
              follow up with the useful stuff.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12" data-id="element-361">
          {/* Form */}
          <div className="lg:col-span-7 order-2 lg:order-1" data-id="element-362">
            {succeeded ? <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="rounded-2xl bg-gradient-to-br from-[#1a8fd4]/10 to-[#0a0d12] border border-[#1a8fd4]/30 p-12 text-center" data-id="element-363">
                <CheckCircle2 className="w-16 h-16 text-[#1a8fd4] mx-auto mb-6" data-id="element-364" />
                <h3 className="text-2xl font-semibold text-white mb-3" data-id="element-365">
                  You're on the list.
                </h3>
                <p className="text-base text-white/65 mb-5 max-w-md mx-auto" data-id="element-366">
                  Thanks for reaching out. We'll review your request and follow
                  up with next steps.
                </p>
                <p className="text-sm text-white/45 italic max-w-md mx-auto" data-id="element-367">
                  In the meantime, maybe ask your team which controls they trust
                  most. Then ask how they know.
                </p>
              </motion.div> : <form onSubmit={handleSubmit} className="rounded-2xl bg-[#070a0e] border border-white/5 p-8 md:p-10" data-id="element-368">
                <p className="font-mono text-xs uppercase tracking-widest text-[#1a8fd4] mb-6" data-id="element-369">
                  Request Early Access
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4" data-id="element-370">
                  <Field id="email" name="email" type="email" label="Work email *" required placeholder="you@company.com" data-id="element-371" />
                  <Field id="name" name="name" label="Name" placeholder="Jane Doe" data-id="element-372" />
                  <Field id="company" name="company" label="Company" placeholder="Your company" data-id="element-373" />
                  <Field id="website" name="website" label="Company website" placeholder="company.com" data-id="element-374" />
                  <Field id="role" name="role" label="Role" placeholder="e.g. CISO, Security Engineer" data-id="element-375" />
                  <SelectField id="company_size" name="company_size" label="Company size" options={['1–10', '11–50', '51–200', '201–1,000', '1,001–10,000', '10,000+']} data-id="element-376" />
                </div>

                <div className="mb-4" data-id="element-377">
                  <SelectField id="use_case" name="use_case" label="Primary use case" options={['Attack-path report', 'AI app validation', 'Fix verification', 'Customer security review evidence', 'SOC 2 / ISO readiness', 'Cyber-insurance readiness', 'MSSP / client reporting', 'Internal validation', 'Business unit validation', 'Other']} data-id="element-378" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4" data-id="element-379">
                  <SelectField id="plan" name="plan" label="Interested plan" options={['Snapshot', 'Team', 'Business', 'Scale', 'MSSP', 'Enterprise', 'Not sure yet']} data-id="element-380" />
                  <Field id="stack" name="stack" label="Cloud / identity stack" placeholder="e.g. AWS + Okta" data-id="element-381" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4" data-id="element-382">
                  <SelectField id="ai_app" name="ai_app" label="AI app to validate?" options={['Yes', 'No', 'Maybe']} data-id="element-383" />
                  <SelectField id="mssp" name="mssp" label="MSSP or vCISO?" options={['Yes', 'No']} data-id="element-384" />
                </div>

                <div className="mb-6" data-id="element-385">
                  <label htmlFor="prompted" className="block font-mono text-xs uppercase tracking-widest text-white/40 mb-2" data-id="element-386">
                    What prompted your interest?
                  </label>
                  <textarea id="prompted" name="prompted" rows={3} placeholder="Customer review, AI launch, SOC 2 prep, cyber-insurance renewal..." className="w-full bg-[#050608] border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-[#1a8fd4]/50 focus:ring-1 focus:ring-[#1a8fd4]/50 transition-all resize-none" data-id="element-387" />
                </div>

                <ValidationError errors={state.errors} className="text-sm text-red-400 mb-4" data-id="element-388" />

                <button type="submit" disabled={submitting} className="group w-full flex items-center justify-between gap-2 px-6 py-4 bg-white text-black font-medium rounded-full hover:bg-white/90 transition-colors disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-389">
                  <span data-id="element-390">
                    {submitting ? 'Requesting…' : 'Request Early Access'}
                  </span>
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" data-id="element-391" />
                </button>

                <p className="mt-4 text-xs text-white/40 text-center italic max-w-md mx-auto" data-id="element-392">
                  We'll use this to contact you about Periscan. We will not sell
                  your information or send you a 14-email "cyber resilience"
                  novella.
                </p>
              </form>}
          </div>

          {/* Sidebar info */}
          <div className="lg:col-span-5 order-1 lg:order-2 space-y-8" data-id="element-393">
            <div data-id="element-394">
              <p className="font-mono text-xs uppercase tracking-widest text-white/40 mb-4" data-id="element-395">
                Focus areas
              </p>
              <div className="space-y-2" data-id="element-396">
                {focus.map((f, i) => <div key={i} className="flex items-center gap-3 py-1.5 border-b border-white/5 last:border-0" data-id="element-397">
                    <span className="font-mono text-[10px] text-[#1a8fd4]/60" data-id="element-398">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span className="text-sm text-white/70" data-id="element-399">{f}</span>
                  </div>)}
              </div>
            </div>

            <div data-id="element-400">
              <p className="font-mono text-xs uppercase tracking-widest text-white/40 mb-4" data-id="element-401">
                Ideal early access teams
              </p>
              <div className="flex flex-wrap gap-1.5" data-id="element-402">
                {ideal.map((t, i) => <span key={i} className="px-2.5 py-1 text-xs text-white/60 bg-white/[0.03] border border-white/5 rounded-md" data-id="element-403">
                    {t}
                  </span>)}
              </div>
            </div>

            <div className="p-6 rounded-xl bg-gradient-to-br from-[#1a8fd4]/10 to-transparent border border-[#1a8fd4]/20" data-id="element-404">
              <p className="font-mono text-xs uppercase tracking-widest text-[#1a8fd4] mb-3" data-id="element-405">
                Availability
              </p>
              <p className="text-sm text-white/70 leading-relaxed" data-id="element-406">
                Periscan is in pre-release. The first release focuses on
                attack-path reports, AI app validation reports, fix
                verification, and evidence-ready reports. Additional
                capabilities will roll out in later beta phases.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function Field({
  id,
  name,
  label,
  type = 'text',
  placeholder,
  required
}: {
  id: string;
  name: string;
  label: string;
  type?: string;
  placeholder?: string;
  required?: boolean;
}) {
  return <div data-id="element-407">
      <label htmlFor={id} className="block font-mono text-xs uppercase tracking-widest text-white/40 mb-2" data-id="element-408">
        {label}
      </label>
      <input id={id} name={name} type={type} required={required} placeholder={placeholder} className="w-full bg-[#050608] border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-[#1a8fd4]/50 focus:ring-1 focus:ring-[#1a8fd4]/50 transition-all" data-id="element-409" />
    </div>;
}
function SelectField({
  id,
  name,
  label,
  options
}: {
  id: string;
  name: string;
  label: string;
  options: string[];
}) {
  return <div data-id="element-410">
      <label htmlFor={id} className="block font-mono text-xs uppercase tracking-widest text-white/40 mb-2" data-id="element-411">
        {label}
      </label>
      <select id={id} name={name} className="w-full bg-[#050608] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#1a8fd4]/50 focus:ring-1 focus:ring-[#1a8fd4]/50 transition-all appearance-none" data-id="element-412">
        <option value="" data-id="element-413">Select…</option>
        {options.map(o => <option key={o} value={o} data-id="element-414">
            {o}
          </option>)}
      </select>
    </div>;
}